import { ConfigurationValues, InverterConfig, JSONRecordData } from '../Log/graph.util';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Modal,
  Select,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import React, { useContext, useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import MenuItem from '@mui/material/MenuItem';
import axiosConfig from '../../../Resources/axiosConfig';
import { UserContext } from '../../../contexts/userContext';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import {
  buildSodistoreProPreset,
  getPresetsForDevice,
  parseBatterySnTree,
  PresetConfig
} from '../Information/installationSetupUtils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker, TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import CurrentPriceChart from './CurrentPriceChart';

interface SodistoreHomeConfigurationProps {
  values: JSONRecordData;
  id: number;
  installation: I_Installation;
}

function SodistoreHomeConfigurationV2(props: SodistoreHomeConfigurationProps) {
  const intl = useIntl();
  if (props.values === null) {
    return null;
  }

  const device = props.installation.device;

  const OperatingPriorityOptions =
    device === 3 || device === 4
      ? ['LoadPriority', 'BatteryPriority', 'GridPriority']
      : [];

  // Sinexcel S3 stores WorkingMode enum names — map them to Growatt-style display names
  const sinexcelS3ToDisplayName: Record<string, string> = {
    'SpontaneousSelfUse': 'LoadPriority',
    'TimeChargeDischarge': 'BatteryPriority',
    'PvPriorityCharging': 'GridPriority',
  };

  // Dynamic Pricing Mode — backend enum values with UI labels
  const DynamicPricingOptions = ['Disabled', 'SpotPrice', 'Tou'] as const;
  const dynamicPricingLabelKey: Record<string, string> = {
    Disabled: 'dynamicPricingOff',
    SpotPrice: 'dynamicPricingSpotPrice',
    Tou: 'dynamicPricingTou',
  };

  const [errors, setErrors] = useState({
    minimumSoC: false,
    gridSetPoint: false
  });

  const SetErrorForField = (field_name, state) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field_name]: state
    }));
  };
  const theme = useTheme();
  const [formDirty, setFormDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [dateSelectionError, setDateSelectionError] = useState('');
  const [isErrorDateModalOpen, setErrorDateModalOpen] = useState(false);
  const context = useContext(UserContext);
  const { currentUser, setUser } = context;
  const { product, setProduct } = useContext(ProductIdContext);

  // Resolve S3 OperatingPriority to display index (Sinexcel uses different enum names)
  const resolveOperatingPriorityIndex = (s3Value: string) => {
    const displayName = device === 4 ? (sinexcelS3ToDisplayName[s3Value] ?? s3Value) : s3Value;
    return OperatingPriorityOptions.indexOf(displayName);
  };

  // Storage key for pending config (optimistic update)
  const pendingConfigKey = `pendingConfig_${props.id}`;

  // Hardware topology — derived from Information tab (single source of truth).
  const isSodistorePro = product === 5;
  const installationModel = props.installation.installationModel;
  const presetConfig: PresetConfig | null = isSodistorePro
    ? (installationModel && parseInt(installationModel, 10) > 0
        ? buildSodistoreProPreset(parseInt(installationModel, 10))
        : null)
    : (getPresetsForDevice(device)[installationModel] || null);
  const inverterCount = presetConfig?.length ?? 1;

  // Default battery limit per cluster: 95A per battery for both charge and discharge
  // (so 1-battery cluster → 95A, 2-battery cluster → 190A).
  const DEFAULT_CURRENT_PER_BATTERY = 95;
  const defaultClusterCurrent = (batteryCount: number) => batteryCount * DEFAULT_CURRENT_PER_BATTERY;

  // (No standalone preset-based builder — overlayTopology() handles the empty case
  // and seeds defaults using the actual installed battery count from the Information tab,
  // not the preset slot count.)

  // Helper to build form values from S3 data.
  // V2 reads only fields it actually consumes — legacy flat keys (InverterNumber,
  // BatteriesCount, MaximumChargingCurrent, etc.) are no longer wired through.
  const getS3Values = (): Partial<ConfigurationValues> => {
    // Read per-inverter Clusters/PvCount from each Devices.InverterN entry on disk.
    const cfgDevices = (props.values.Config as any).Devices as { [k: string]: any } | undefined;
    const cfgFromDisk: { [k: string]: InverterConfig } | undefined = cfgDevices
      ? Object.fromEntries(
          Object.entries(cfgDevices)
            .filter(([k, v]: [string, any]) => k.startsWith('Inverter') && (v?.Clusters || v?.PvCount != null))
            .map(([k, v]: [string, any]) => [k, { Clusters: v.Clusters ?? {}, PvCount: v.PvCount ?? 0 }])
        )
      : undefined;
    const hasDevicesData = cfgFromDisk && Object.keys(cfgFromDisk).length > 0;
    return {
      minimumSoC: props.values.Config.MinSoc,
      // Information tab is source of truth for topology + per-cluster current defaults.
      devices: overlayTopology(hasDevicesData ? cfgFromDisk : undefined),
      operatingPriority: resolveOperatingPriorityIndex(
        props.values.Config.OperatingPriority
      ),
      timeChargeandDischargePower: props.values.Config?.TimeChargeandDischargePower ?? 0,
      startTimeChargeandDischargeDayandTime: (() => {
        const raw = props.values.Config?.StartTimeChargeandDischargeDayandTime;
        const parsed = raw ? dayjs(raw) : null;
        return parsed && parsed.year() >= 2020 ? parsed.toDate() : new Date();
      })(),
      stopTimeChargeandDischargeDayandTime: (() => {
        const raw = props.values.Config?.StopTimeChargeandDischargeDayandTime;
        const parsed = raw ? dayjs(raw) : null;
        return parsed && parsed.year() >= 2020 ? parsed.toDate() : new Date();
      })(),
      controlPermission: String(props.values.Config.ControlPermission).toLowerCase() === "true",
      relay1: String((props.values.Config as any).Relay1).toLowerCase() === "true",
      relay2: String((props.values.Config as any).Relay2).toLowerCase() === "true",
      relay3: String((props.values.Config as any).Relay3).toLowerCase() === "true",
      relay4: String((props.values.Config as any).Relay4).toLowerCase() === "true",
      dynamicPricingMode: (props.values.Config as any).DynamicPricingMode ?? 'Disabled',
      currentPrice: (props.values.Config as any).CurrentPrice?.toString() ?? '',
      priceToSell: (props.values.Config as any).PriceToSell?.toString() ?? '',
      priceToBuy: (props.values.Config as any).PriceToBuy?.toString() ?? '',
      timeToSellFrom: (props.values.Config as any).TimeToSellFrom ?? '',
      timeToSellTo: (props.values.Config as any).TimeToSellTo ?? '',
      timeToBuyFrom: (props.values.Config as any).TimeToBuyFrom ?? '',
      timeToBuyTo: (props.values.Config as any).TimeToBuyTo ?? '',
    };
  };

  // Restore pending config from localStorage, converting date strings back to Date objects.
  // Returns { values, s3ConfigSnapshot } or null if no pending config.
  const restorePendingConfig = () => {
    try {
      const pendingStr = localStorage.getItem(pendingConfigKey);
      if (!pendingStr) return null;

      const pending = JSON.parse(pendingStr);
      const v = pending.values;
      const values: Partial<ConfigurationValues> = {
        ...v,
        // JSON.stringify converts Date→string; restore them back to Date objects
        startTimeChargeandDischargeDayandTime:
          v.startTimeChargeandDischargeDayandTime
            ? dayjs(v.startTimeChargeandDischargeDayandTime).toDate()
            : null,
        stopTimeChargeandDischargeDayandTime:
          v.stopTimeChargeandDischargeDayandTime
            ? dayjs(v.stopTimeChargeandDischargeDayandTime).toDate()
            : null,
      };

      return { values, s3ConfigSnapshot: pending.s3ConfigSnapshot || null };
    } catch (e) {
      console.error('[Config:restore] Failed to parse localStorage', e);
      localStorage.removeItem(pendingConfigKey);
      return null;
    }
  };

  // Fingerprint S3 Config for change detection (not value comparison)
  const getS3ConfigFingerprint = () => JSON.stringify(props.values.Config);

  // Overlay Information-tab-derived topology onto a `devices` config object.
  // Battery counts come from the SN tree (filled SNs); PvCount comes from pvStringsPerInverter.
  // Existing per-cluster current limits are preserved.
  const overlayTopology = (
    devices: { [k: string]: InverterConfig } | undefined
  ): { [k: string]: InverterConfig } | undefined => {
    if (!presetConfig) return devices;
    const tree = parseBatterySnTree(props.installation.batterySerialNumbers || '', presetConfig);
    const pvStrings = (props.installation.pvStringsPerInverter || '')
      .split(',')
      .map((s) => s.trim());
    const out: { [k: string]: InverterConfig } = {};
    presetConfig.forEach((clusters, invIdx) => {
      const invKey = `Inverter${invIdx + 1}`;
      const existingInv = devices?.[invKey];
      const cls: { [k: string]: any } = {};
      clusters.forEach((slotCount, clIdx) => {
        const clKey = `Cluster${clIdx + 1}`;
        const filled = (tree[invIdx]?.[clIdx] ?? []).filter((s) => s !== '').length;
        const existingCl = existingInv?.Clusters?.[clKey];
        // Default current = (effective battery count) × 95A.
        // Partial fill: use the actual installed count (real safety limit).
        // Empty cluster (no SNs yet): fall back to the cluster's ideal slot count
        // so a freshly-created installation gets a sensible default.
        const effectiveCount = filled > 0 ? filled : slotCount;
        const defaultCurrent = defaultClusterCurrent(effectiveCount);
        cls[clKey] = {
          BatteryCount: filled,
          MaxChargingCurrent: existingCl?.MaxChargingCurrent ?? defaultCurrent,
          MaxDischargingCurrent: existingCl?.MaxDischargingCurrent ?? defaultCurrent,
        };
      });
      out[invKey] = {
        Clusters: cls,
        PvCount: parseInt(pvStrings[invIdx] || '0', 10) || 0,
      };
    });
    return out;
  };

  // Initialize form from localStorage (if pending submit exists) or from S3
  // This runs in the useState initializer so the component never renders stale values
  const [formValues, setFormValues] = useState<Partial<ConfigurationValues>>(() => {
    const pending = restorePendingConfig();
    const s3 = getS3Values();
    if (pending) {
      // Check if S3 has new data since submit (fingerprint changed from snapshot)
      const currentFingerprint = getS3ConfigFingerprint();
      const s3Changed = pending.s3ConfigSnapshot && currentFingerprint !== pending.s3ConfigSnapshot;

      if (s3Changed) {
        // Device uploaded new data since our submit — trust S3 (device is authority)
        localStorage.removeItem(pendingConfigKey);
        return s3;
      }

      // S3 still has same data as when we submitted — show pending values
      return pending.values;
    }
    return s3;
  });

  // When S3 data updates (polled every 60s), reconcile with any pending localStorage.
  // Strategy: device is the authority. Once S3 Config changes from the snapshot taken at
  // submit time, the device has uploaded new data — trust S3 regardless of values.
  // Skip reset if the user is actively editing (formDirty).
  useEffect(() => {
    if (formDirty) return;

    const s3Values = getS3Values();
    const pending = restorePendingConfig();

    if (pending) {
      const currentFingerprint = getS3ConfigFingerprint();
      const s3Changed = pending.s3ConfigSnapshot && currentFingerprint !== pending.s3ConfigSnapshot;
      if (s3Changed) {
        // S3 Config changed from snapshot → device uploaded new data → trust S3
        localStorage.removeItem(pendingConfigKey);
        setFormValues(s3Values);
      } else {
        // S3 still has same data as at submit time — keep showing pending values
        setFormValues(pending.values);
      }
      return;
    }

    // No pending config — trust S3 (source of truth)
    setFormValues(s3Values);
  }, [props.values]);

  const handleOperatingPriorityChange = (event) => {
    setFormDirty(true);
    setFormValues({
      ...formValues,
      ['operatingPriority']: OperatingPriorityOptions.indexOf(
        event.target.value
      )
    });
  };

// Add time validation function — only relevant for Sinexcel BatteryPriority
  const validateTimeOnly = () => {
    if (device === 4 &&
      OperatingPriorityOptions[formValues.operatingPriority] === 'BatteryPriority' &&
      formValues.startTimeChargeandDischargeDayandTime &&
      formValues.stopTimeChargeandDischargeDayandTime) {
      const startHours = formValues.startTimeChargeandDischargeDayandTime.getHours();
      const startMinutes = formValues.startTimeChargeandDischargeDayandTime.getMinutes();
      const stopHours = formValues.stopTimeChargeandDischargeDayandTime.getHours();
      const stopMinutes = formValues.stopTimeChargeandDischargeDayandTime.getMinutes();

      const startTimeInMinutes = startHours * 60 + startMinutes;
      const stopTimeInMinutes = stopHours * 60 + stopMinutes;

      if (startTimeInMinutes >= stopTimeInMinutes) {
        setDateSelectionError(intl.formatMessage({ id: 'stopTimeMustBeLater' }));
        setErrorDateModalOpen(true);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    if (!validateTimeOnly()) {
      return;
    }
    // Re-overlay Information-tab topology at submit time, so battery counts and PV count
    // are always the latest from the Information tab (it's the source of truth).
    const devices = overlayTopology(formValues.devices);
    const configurationToSend: Partial<ConfigurationValues> = {
      minimumSoC: formValues.minimumSoC,
      devices,
      operatingPriority: formValues.operatingPriority,
      timeChargeandDischargePower: formValues.timeChargeandDischargePower,
      startTimeChargeandDischargeDayandTime: formValues.startTimeChargeandDischargeDayandTime
        ? new Date(formValues.startTimeChargeandDischargeDayandTime.getTime() - formValues.startTimeChargeandDischargeDayandTime.getTimezoneOffset() * 60000)
        : null,
      stopTimeChargeandDischargeDayandTime: formValues.stopTimeChargeandDischargeDayandTime
        ? new Date(formValues.stopTimeChargeandDischargeDayandTime.getTime() - formValues.stopTimeChargeandDischargeDayandTime.getTimezoneOffset() * 60000)
        : null,
      controlPermission:formValues.controlPermission,
      // Relay control — only send for Sinexcel (device=4); leaving them undefined
      // ensures they're omitted from the UDP payload for Growatt installations.
      ...(device === 4 ? {
        relay1: Boolean(formValues.relay1),
        relay2: Boolean(formValues.relay2),
        relay3: Boolean(formValues.relay3),
        relay4: Boolean(formValues.relay4),
      } : {}),
      dynamicPricingMode: formValues.dynamicPricingMode,
      currentPrice: formValues.currentPrice,
      priceToSell: formValues.priceToSell,
      priceToBuy: formValues.priceToBuy,
      timeToSellFrom: formValues.timeToSellFrom,
      timeToSellTo: formValues.timeToSellTo,
      timeToBuyFrom: formValues.timeToBuyFrom,
      timeToBuyTo: formValues.timeToBuyTo,
    };

    setLoading(true);
    const res = await axiosConfig
      .post(
        `/EditInstallationConfig?installationId=${props.id}&product=${product}`,
        configurationToSend
      )
      .catch((err) => {
        if (err.response) {
          setError(true);
          setLoading(false);
        }
      });

    if (res) {
      setUpdated(true);
      setLoading(false);
      setFormDirty(false);

      // Save submitted values + S3 snapshot to localStorage for optimistic UI update.
      // s3ConfigSnapshot = fingerprint of S3 Config at submit time.
      // When S3 Config changes from this snapshot, the device has uploaded new data.
      const cachePayload = {
        values: formValues,
        submittedAt: Date.now(),
        s3ConfigSnapshot: getS3ConfigFingerprint(),
      };
      localStorage.setItem(pendingConfigKey, JSON.stringify(cachePayload));
    }
  };

  const handleChange = (e) => {
    setFormDirty(true);
    const { name, value } = e.target;

    if (name === 'minimumSoC') {
      const numValue = parseFloat(value);

      // invalid characters or not a number
      if (/[^0-9.]/.test(value) || isNaN(numValue)) {
        SetErrorForField(name, 'Invalid number format');
      } else {
        const minsocRanges = {
          3: { min: 10, max: 30 },
          4: { min: 5, max: 100 },
        };

        const { min, max } = minsocRanges[device] || { min: 10, max: 30 };

        if (numValue < min || numValue > max) {
          SetErrorForField(name, `Value should be between ${min}-${max}%`);
        } else {
          // ✅ valid → clear error
          SetErrorForField(name, '');
        }
      }
    }

    setFormValues(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTimeChargeDischargeChange = (name: string, value: any) => {
    setFormDirty(true);
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOkOnErrorDateModal = () => {
    setErrorDateModalOpen(false);
  };

  return (
    <Container maxWidth="xl">
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        {isErrorDateModalOpen && (
          <Modal open={isErrorDateModalOpen} onClose={() => {}}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 450,
                bgcolor: 'background.paper',
                borderRadius: 4,
                boxShadow: 24,
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Typography
                variant="body1"
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                {dateSelectionError}
              </Typography>

              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={handleOkOnErrorDateModal}
              >
                Ok
              </Button>
            </Box>
          </Modal>
        )}

        <Grid item xs={12} md={12}>
          <CardContent>
            <Box
              component="form"
              sx={{
                '& .MuiTextField-root': { m: 1, width: 390 }
              }}
              noValidate
              autoComplete="off"
            >
              <>
                <div style={{ marginBottom: '5px' }}>
                  <FormControlLabel
                    labelPlacement="start"
                    control={
                      <Switch
                        name="controlPermission"
                        checked={Boolean(formValues.controlPermission)}
                        onChange={(e) => {
                          setFormDirty(true);
                          setFormValues((prev) => ({
                            ...prev,
                            controlPermission: e.target.checked,
                          }));
                        }
                        }
                        sx={{ transform: "scale(1.4)", marginLeft: "15px" }}
                      />
                    }
                    sx={{ ml: 0 }}
                    label={
                      <FormattedMessage
                        id="controlPermission"
                        defaultMessage="Control Permission"
                      />
                    }
                  />
                </div>

                {device === 4 && (product === 2 || product === 5) && (
                  <>
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                      <FormattedMessage id="digitalInputs" defaultMessage="Digital Inputs" />
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 1, ml: 1 }}>
                      {[1, 2, 3, 4].map((n) => {
                        const raw = (props.values.Config as any)[`DigitalInput${n}`];
                        // No datapath (field absent/null) → grey; otherwise green/red
                        const indicator =
                          raw == null
                            ? '⚪'
                            : String(raw).toLowerCase() === 'true'
                              ? '🟢'
                              : '🔴';
                        return (
                          <Box
                            key={`digitalInput${n}`}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                          >
                            <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>
                              {indicator}
                            </span>
                            <Typography component="span">
                              <FormattedMessage
                                id={`digitalInput${n}`}
                                defaultMessage={`Digital Input ${n}`}
                              />
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}

                {device === 4 && (
                  <>
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                      <FormattedMessage id="relayControl" defaultMessage="Relay Control" />
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 1, ml: 1 }}>
                      {[1, 2, 3, 4].map((n) => {
                        const key = `relay${n}` as 'relay1' | 'relay2' | 'relay3' | 'relay4';
                        return (
                          <FormControlLabel
                            key={key}
                            labelPlacement="start"
                            control={
                              <Switch
                                name={key}
                                checked={Boolean(formValues[key])}
                                onChange={(e) => {
                                  setFormDirty(true);
                                  setFormValues((prev) => ({
                                    ...prev,
                                    [key]: e.target.checked,
                                  }));
                                }}
                                sx={{ transform: 'scale(1.2)', ml: 1 }}
                              />
                            }
                            sx={{ ml: 0, mr: 0 }}
                            label={
                              <FormattedMessage
                                id={`relay${n}`}
                                defaultMessage={`Relay ${n}`}
                              />
                            }
                          />
                        );
                      })}
                    </Box>
                  </>
                )}

                {(device === 3 || device === 4) && (
                  <>
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                      <FormattedMessage id="batteryLimits" defaultMessage="Battery Limits" />
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </>
                )}

                <div style={{ marginBottom: '5px' }}>
                  {/*<TextField*/}
                  {/*  label={*/}
                  {/*    <FormattedMessage*/}
                  {/*      id="minimum_soc "*/}
                  {/*      defaultMessage="Minimum SoC (%)"*/}
                  {/*    />*/}
                  {/*  }*/}
                  {/*  name="minimumSoC"*/}
                  {/*  value={formValues.minimumSoC}*/}
                  {/*  onChange={handleChange}*/}
                  {/*  helperText={*/}
                  {/*    errors.minimumSoC ? (*/}
                  {/*      <span style={{ color: 'red' }}>*/}
                  {/*        Value should be between {device === 4 ? '5–100' : '10–30'}%*/}
                  {/*      </span>*/}
                  {/*    ) : (*/}
                  {/*      ''*/}
                  {/*    )*/}
                  {/*  }*/}
                  {/*  fullWidth*/}
                  {/*/>*/}
                  <TextField
                    label={intl.formatMessage({ id: 'minimumSocPercent' })}
                    name="minimumSoC"
                    value={formValues.minimumSoC}
                    onChange={handleChange}
                    error={Boolean(errors.minimumSoC)}
                    helperText={errors.minimumSoC}
                    fullWidth
                  />

                </div>

                {(device === 3 || device === 4) ? (
                  // Per-cluster, per-inverter charging/discharging current limits — nested config.
                  Array.from({ length: inverterCount }, (_, invIdx) => {
                    const invKey = `Inverter${invIdx + 1}`;
                    const clusters = presetConfig?.[invIdx] ?? [0];
                    return (
                      <Accordion
                        key={`limits-${invKey}`}
                        defaultExpanded={false}
                        sx={{ ml: 1, mr: 1, mt: 1 }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            '& .MuiAccordionSummary-content': { flexGrow: 0, justifyContent: 'flex-start' },
                            justifyContent: 'flex-start',
                            '& .MuiAccordionSummary-expandIconWrapper': { ml: 1 }
                          }}
                        >
                          <Typography sx={{ fontWeight: 'bold' }}>
                            <FormattedMessage id="inverterN" defaultMessage="Inverter {n}" values={{ n: invIdx + 1 }} />
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {clusters.map((_slotCount, clIdx) => {
                            const clKey = `Cluster${clIdx + 1}`;
                            const cluster = formValues.devices?.[invKey]?.Clusters?.[clKey];
                            // Hide clusters with no installed batteries — Information tab is source of truth.
                            if (cluster?.BatteryCount === 0) return null;
                            const charge = cluster?.MaxChargingCurrent ?? '';
                            const discharge = cluster?.MaxDischargingCurrent ?? '';
                            const setClusterField = (
                              field: 'MaxChargingCurrent' | 'MaxDischargingCurrent',
                              v: string
                            ) => {
                              if (v !== '' && !/^\d*\.?\d*$/.test(v)) return;
                              setFormDirty(true);
                              setFormValues((prev) => {
                                const devices = { ...(prev.devices ?? {}) };
                                const inv = devices[invKey]
                                  ?? { Clusters: {}, PvCount: 0 };
                                const cls = { ...(inv.Clusters ?? {}) };
                                const existing = cls[clKey] ?? {
                                  BatteryCount: presetConfig?.[invIdx]?.[clIdx] ?? 0,
                                  MaxChargingCurrent: 0,
                                  MaxDischargingCurrent: 0,
                                };
                                if (v === '') {
                                  // Drop the field while editing so the input renders empty,
                                  // not "0". On submit, anything still missing falls back to 0.
                                  const next: any = { ...existing };
                                  delete next[field];
                                  cls[clKey] = next;
                                } else {
                                  cls[clKey] = { ...existing, [field]: parseFloat(v) };
                                }
                                devices[invKey] = { ...inv, Clusters: cls };
                                return { ...prev, devices };
                              });
                            };
                            return (
                              <div key={`cl-limits-${invKey}-${clKey}`} style={{ marginBottom: '5px' }}>
                                <Typography variant="subtitle2" sx={{ mt: 1, ml: 1 }}>
                                  <FormattedMessage id="clusterN" defaultMessage="Cluster {n}" values={{ n: clIdx + 1 }} />
                                </Typography>
                                <TextField
                                  label={intl.formatMessage({ id: 'maximumChargingCurrentPerClusterLabel' })}
                                  value={charge}
                                  onChange={(e) => setClusterField('MaxChargingCurrent', e.target.value)}
                                  fullWidth
                                />
                                <TextField
                                  label={intl.formatMessage({ id: 'maximumDischargingCurrentPerClusterLabel' })}
                                  value={discharge}
                                  onChange={(e) => setClusterField('MaxDischargingCurrent', e.target.value)}
                                  fullWidth
                                />
                              </div>
                            );
                          })}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })
                ) : (
                  <>
                    <div style={{ marginBottom: '5px' }}>
                      <TextField
                        label={intl.formatMessage({ id: 'maximumChargingCurrentPerBattery' })}
                        name="maximumChargingCurrent"
                        value={formValues.maximumChargingCurrent}
                        onChange={handleChange}
                        fullWidth
                      />
                    </div>

                    <div style={{ marginBottom: '5px' }}>
                      <TextField
                        label={intl.formatMessage({ id: 'maximumDischargingCurrentPerBattery' })}
                        name="maximumDischargingCurrent"
                        value={formValues.maximumDischargingCurrent}
                        onChange={handleChange}
                        fullWidth
                      />
                    </div>
                  </>
                )}

                {(device === 3 || device === 4) && (
                  <>
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                      <FormattedMessage id="systemSettings" defaultMessage="System Settings" />
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </>
                )}

                <div style={{ marginBottom: '5px', marginTop: '10px' }}>
                  <FormControl fullWidth sx={{ marginLeft: 1, width: 390 }}>
                    <InputLabel
                      sx={{
                        fontSize: 14,
                        backgroundColor: 'white'
                      }}
                    >
                      <FormattedMessage
                        id="operating_priority"
                        defaultMessage="Operating Priority"
                      />
                    </InputLabel>
                    <Select
                      value={
                        OperatingPriorityOptions[formValues.operatingPriority]
                      }
                      onChange={handleOperatingPriorityChange}
                    >
                      {OperatingPriorityOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </>

              {/* --- Sinexcel + BatteryPriority (maps to TimeChargeDischarge on device) --- */}
              {device === 4 &&
                OperatingPriorityOptions[formValues.operatingPriority] ===
                'BatteryPriority' && (
                  <>
                    {/* Power input*/}
                    <div style={{ marginBottom: '5px' }}>
                      <TextField
                        label={intl.formatMessage({ id: 'powerPerInverterKW' })}
                        name="timeChargeandDischargePower"
                        value={formValues.timeChargeandDischargePower}
                        onChange={(e) =>
                          handleTimeChargeDischargeChange(e.target.name, e.target.value)
                        }
                        helperText={intl.formatMessage({ id: 'perInverter' })}
                        fullWidth
                      />
                    </div>

                    {/* Start DateTime */}
                    <div style={{ marginBottom: '5px' }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          ampm={false}
                          label={intl.formatMessage({ id: 'startDateTime' })}
                          value={
                            formValues.startTimeChargeandDischargeDayandTime
                              ? dayjs(formValues.startTimeChargeandDischargeDayandTime)
                              : null
                          }
                          onChange={(newValue) =>
                            setFormValues((prev) => ({
                              ...prev,
                              startTimeChargeandDischargeDayandTime: newValue
                                ? newValue.toDate()
                                : null,
                            }))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{
                                marginTop: 2,
                                width: '100%',
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>

                    {/* Stop DateTime */}
                    <div style={{ marginBottom: '5px' }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          ampm={false}
                          label={intl.formatMessage({ id: 'stopDateTime' })}
                          value={
                            formValues.stopTimeChargeandDischargeDayandTime
                              ? dayjs(formValues.stopTimeChargeandDischargeDayandTime)
                              : null
                          }
                          onChange={(newValue) =>
                            setFormValues((prev) => ({
                              ...prev,
                              stopTimeChargeandDischargeDayandTime: newValue
                                ? newValue.toDate()
                                : null,
                            }))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{
                                marginTop: 2,
                                width: '100%',
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </>
                )}

              {/* --- Growatt + Sinexcel under LoadPriority: Dynamic Pricing --- */}
              {(device === 3 || device === 4) &&
                OperatingPriorityOptions[formValues.operatingPriority] === 'LoadPriority' && (
                  <>
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                      <FormattedMessage id="dynamicPricing" defaultMessage="Dynamic Pricing" />
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <div style={{ marginBottom: '5px', marginTop: '10px' }}>
                      <FormControl fullWidth sx={{ marginLeft: 1, width: 390 }}>
                        <InputLabel sx={{ fontSize: 14, backgroundColor: 'white' }}>
                          <FormattedMessage id="dynamicPricingMode" defaultMessage="Dynamic Pricing Mode" />
                        </InputLabel>
                        <Select
                          value={formValues.dynamicPricingMode ?? 'Disabled'}
                          onChange={(e) => {
                            setFormDirty(true);
                            setFormValues((prev) => ({
                              ...prev,
                              dynamicPricingMode: e.target.value as string,
                            }));
                          }}
                        >
                          {DynamicPricingOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {intl.formatMessage({ id: dynamicPricingLabelKey[opt] })}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>

                    {formValues.dynamicPricingMode === 'Tou' && (
                      <>
                        {(() => {
                          const renderTimeField = (
                            labelId: string,
                            key: 'timeToSellFrom' | 'timeToSellTo' | 'timeToBuyFrom' | 'timeToBuyTo'
                          ) => {
                            const raw = formValues[key];
                            const parsed = raw ? dayjs(raw, 'HH:mm') : null;
                            return (
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                  ampm={false}
                                  label={intl.formatMessage({ id: labelId })}
                                  value={parsed && parsed.isValid() ? parsed : null}
                                  onChange={(newValue) => {
                                    setFormDirty(true);
                                    setFormValues((prev) => ({
                                      ...prev,
                                      [key]: newValue ? newValue.format('HH:mm') : '',
                                    }));
                                  }}
                                  renderInput={(params) => (
                                    <TextField {...params} sx={{ marginTop: 2, width: '100%' }} />
                                  )}
                                />
                              </LocalizationProvider>
                            );
                          };

                          return (
                            <>
                              <Typography variant="subtitle2" sx={{ mt: 2, ml: 1 }}>
                                <FormattedMessage id="timeToSell" defaultMessage="Time to Sell" />
                              </Typography>
                              <div style={{ display: 'flex', gap: 8, marginLeft: 8 }}>
                                {renderTimeField('timeFrom', 'timeToSellFrom')}
                                {renderTimeField('timeTo', 'timeToSellTo')}
                              </div>

                              <Typography variant="subtitle2" sx={{ mt: 2, ml: 1 }}>
                                <FormattedMessage id="timeToBuy" defaultMessage="Time to Buy" />
                              </Typography>
                              <div style={{ display: 'flex', gap: 8, marginLeft: 8 }}>
                                {renderTimeField('timeFrom', 'timeToBuyFrom')}
                                {renderTimeField('timeTo', 'timeToBuyTo')}
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}

                    {formValues.dynamicPricingMode === 'SpotPrice' && (
                      <>
                        <div style={{ marginBottom: '5px' }}>
                          <TextField
                            label={intl.formatMessage({ id: 'networkProvider' })}
                            value={
                              props.installation.networkProvider ||
                              intl.formatMessage({ id: 'networkProviderSetOnInformationTab' })
                            }
                            InputProps={{ readOnly: true }}
                            fullWidth
                          />
                        </div>

                        <div style={{ marginBottom: '5px' }}>
                          <TextField
                            label={intl.formatMessage({ id: 'currentPrice' })}
                            value={(props.values.Config as any).CurrentPrice?.toString() ?? ''}
                            InputProps={{
                              readOnly: true,
                              endAdornment: <InputAdornment position="end">CHF/kWh</InputAdornment>,
                            }}
                            fullWidth
                          />
                        </div>

                        <div style={{ marginBottom: '15px', marginTop: '10px' }}>
                          <CurrentPriceChart id={props.id} />
                        </div>

                        <div style={{ marginBottom: '5px' }}>
                          <TextField
                            label={intl.formatMessage({ id: 'priceToSell' })}
                            name="priceToSell"
                            value={formValues.priceToSell ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                setFormDirty(true);
                                setFormValues((prev) => ({ ...prev, priceToSell: v }));
                              }
                            }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">CHF/kWh</InputAdornment>,
                            }}
                            fullWidth
                          />
                        </div>

                        <div style={{ marginBottom: '5px' }}>
                          <TextField
                            label={intl.formatMessage({ id: 'priceToBuy' })}
                            name="priceToBuy"
                            value={formValues.priceToBuy ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                setFormDirty(true);
                                setFormValues((prev) => ({ ...prev, priceToBuy: v }));
                              }
                            }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">CHF/kWh</InputAdornment>,
                            }}
                            fullWidth
                          />
                        </div>
                      </>
                    )}
                  </>
                )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: 10
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    marginLeft: '10px'
                  }}
                >
                  <FormattedMessage
                    id="applychanges"
                    defaultMessage="Apply Changes"
                  />
                </Button>
                {loading && (
                  <CircularProgress
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: '20px'
                    }}
                  />
                )}

                {updated && (
                  <Alert
                    severity="success"
                    sx={{
                      ml: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <FormattedMessage id="successfullyAppliedConfig" defaultMessage="Successfully applied configuration file" />
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => setUpdated(false)}
                      sx={{ marginLeft: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Alert>
                )}
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      ml: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <FormattedMessage id="configErrorOccurred" defaultMessage="An error has occurred" />
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => setError(false)}
                      sx={{ marginLeft: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Alert>
                )}
              </div>
            </Box>
          </CardContent>
        </Grid>
      </Grid>
    </Container>
  );
}

export default SodistoreHomeConfigurationV2;
